/* eslint-disable no-nested-ternary */
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import { Box, Button, CardActions } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import ListItemText from '@mui/material/ListItemText'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Checkbox from '@mui/material/Checkbox'

import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { downloadFile } from 'src/v3/utilities/filesystem'
import {
  errorMessageChanged,
  exportUserTranscripts,
  fetchUsers,
  selectActiveLinkedService,
  selectAlphabetizedUsersByLinkedService,
  selectUsersByNames,
  selectUsersStatus,
} from '../manage.slice'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
}

export default () => {
  let dispatch = useAppDispatch()
  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let users = useAppSelector(
    selectAlphabetizedUsersByLinkedService(activeLinkedService),
  )
  let usersStatus = useAppSelector(selectUsersStatus)
  let [userNames, setUserNames] = useState<string[]>([])
  let coursesByName = useAppSelector(selectUsersByNames(userNames))
  let [startDate, setStartDate] = useState(dayjs(new Date()))
  let [endDate, setEndDate] = useState(dayjs(new Date()))
  let [exportStatus, setExportStatus] = useState<
    'uninitialized' | 'exporting' | 'exported'
  >('uninitialized')

  let handleChange = (event: SelectChangeEvent<typeof userNames>) => {
    let {
      target: { value },
    } = event
    setUserNames(typeof value === 'string' ? value.split(',') : value)
  }

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  let submit = () => {
    setExportStatus('exporting')
    dispatch(
      exportUserTranscripts({
        userIDs: coursesByName.map((course) => course.id),
        startDate,
        endDate,
      }),
    )
      .then(() => setExportStatus('exported'))
      .catch((err) => {
        dispatch(
          errorMessageChanged(
            `CSV Export Failed: ${err.response?.data || err}`,
          ),
        )
        setExportStatus('uninitialized')
      })
  }

  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="h6" fontFamily="revert" textAlign="center">
        Export Transcripts
      </Typography>
      <div className="flex flex-col justify-center mt-2">
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="demo-multiple-checkbox-label">Users</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={userNames}
            onChange={handleChange}
            input={<OutlinedInput label="Users" />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.name}>
                <Checkbox checked={userNames.indexOf(user.name) > -1} />
                <ListItemText primary={user.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ pt: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: '100%' }}
              label="Start Date"
              value={startDate}
              onChange={(v) => setStartDate(v as dayjs.Dayjs)}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ pt: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: '100%' }}
              label="End Date"
              value={endDate}
              onChange={(v) => setEndDate(v as dayjs.Dayjs)}
            />
          </LocalizationProvider>
        </Box>
      </div>
      <CardActions>
        <Button
          disabled={exportStatus === 'exporting'}
          variant="outlined"
          color="primary"
          fullWidth
          onClick={submit}
        >
          {exportStatus === 'exporting'
            ? 'Exporting...'
            : exportStatus === 'exported'
              ? 'Exported'
              : 'Export'}
        </Button>
      </CardActions>
    </Card>
  )
}
